/**
 * Application bootstrap. Wires URL params → API → state → Rive + Pixi layers,
 * then hands off to the orchestrator. Mirrors apps/amg_fishPrawnCrab_v2/lib/main.dart.
 *
 * Note: there are no DOM bet zones — those live inside `bettingui.riv` and
 * emit `placeBetClick {betID}` events.
 */
import { Api } from './net/api';
import { MultiplayerHub } from './net/multiplayerHub';
import { GameState } from './game/gameState';
import { GameOrchestrator } from './game/gameOrchestrator';
import { loadUrlParams } from './game/urlParams';
import { RiveLayer } from './rive/riveLayer';
import { PixiLayer } from './pixi/pixiLayer';
import { ChipLayer } from './pixi/chipLayer';
import { Hud } from './ui/hud';
import { TableControls } from './ui/controls';
import { GameTimerWidget } from './ui/timerWidget';
import { GameResultPanel } from './ui/gameResultPanel';
import { GameLogWidget } from './ui/gameLogWidget';
import { PerfHud } from './perf/perfHud';
import { SoundManager } from './audio/soundManager';
import { SoundToggle } from './ui/soundToggle';
import {
  bindGameSounds,
  CHIP_SELECT_SFX,
  START_BET_SFX,
  NO_MORE_BET_SFX,
  SFX_CLIPS,
} from './audio/soundWiring';
import { ResponsiveScaler } from './util/responsiveScaler';
import { MULTIPLAYER_HUB_URL } from './config';
import { debugLog, error as logError } from './util/logger';

declare global {
  interface Window {
    // Defined by the inline loading-splash controller in index.html.
    hideLoadingScreen?: () => void;
  }
}

async function bootstrap(): Promise<void> {
  if (!('WebGL2RenderingContext' in window)) {
    showFatal('Your browser does not support WebGL2. Please use a modern browser.');
    return;
  }

  const scaler = new ResponsiveScaler('stage');
  scaler.attach();

  const params = loadUrlParams();
  debugLog('[main] URL params', params);

  const riveMain = document.getElementById('rive-canvas-main') as HTMLCanvasElement;
  const riveTopBowl = document.getElementById('rive-canvas-topbowl') as HTMLCanvasElement;
  const riveBetting = document.getElementById('rive-canvas-betting') as HTMLCanvasElement;
  const pixiCanvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
  if (!riveMain || !riveTopBowl || !riveBetting || !pixiCanvas) {
    showFatal('Stage canvases missing from DOM.');
    return;
  }

  const rive = new RiveLayer({
    canvases: {
      main: riveMain,
      topbowl: riveTopBowl,
      betting: riveBetting,
    },
  });
  const pixi = new PixiLayer(pixiCanvas);

  const perfHud = new PerfHud();
  perfHud.start();

  const state = new GameState();

  // ---- SFX (external clips only). The toggle gates our SoundManager; it does
  //      NOT touch the .riv. Audio is unlocked on the first user gesture so the
  //      first programmatic play isn't autoplay-blocked.
  const sound = new SoundManager();
  sound.preload(SFX_CLIPS);
  window.addEventListener('pointerdown', sound.unlock, { once: true });
  window.addEventListener('keydown', sound.unlock, { once: true });
  void new SoundToggle({ onChange: (on) => sound.setEnabled(on) });

  // State-driven SFX off the GameState chokepoint (covers DOM + Rive-driven
  // changes to the same state).
  state.on((next, prev) => {
    const chipSelected = next.selectedChipIndex !== prev.selectedChipIndex;
    const betPlaced = next.bets.total > prev.bets.total;
    if (chipSelected || betPlaced) sound.play(CHIP_SELECT_SFX);
    if (next.phase === 'betting' && prev.phase !== 'betting') sound.play(START_BET_SFX);
    if (next.phase === 'bettingClosed' && prev.phase !== 'bettingClosed')
      sound.play(NO_MORE_BET_SFX);
  });

  const api = new Api();
  const hub = new MultiplayerHub(MULTIPLAYER_HUB_URL);
  const hud = new Hud();
  const timer = new GameTimerWidget();
  void new GameLogWidget();

  // DOM result panels — one element instance per type (matches Flutter's
  // three GlobalKey panels: startBet/noMoreBet/winLose split into win+lose).
  const panels = {
    startBet: new GameResultPanel('startBet'),
    noMoreBet: new GameResultPanel('noMoreBet'),
    win: new GameResultPanel('win'),
    lose: new GameResultPanel('lose'),
  };

  let orchestratorRef: GameOrchestrator | null = null;
  const controls = new TableControls({
    onSelectChip: (i) => state.selectChip(i),
    onClear: () => orchestratorRef?.clearAll(),
    onConfirm: () => void orchestratorRef?.confirm(),
  });

  try {
    await Promise.all([pixi.init(), rive.init()]);
  } catch (e) {
    logError('[main] init failed', e);
    showFatal(`Failed to load assets: ${(e as Error)?.message ?? String(e)}`);
    return;
  }
  state.setRiveReady(true);

  // Dice-bowl Rive-event SFX (Move → MoveBowl, CloseBowl → CloseBowl).
  bindGameSounds(rive, sound);

  // Set isMobile on the betting artboard.
  rive.setIsMobile(isMobileDevice());

  const chips = new ChipLayer(pixi.root);

  const orchestrator = new GameOrchestrator({
    state,
    api,
    rive,
    chips,
    hud,
    controls,
    hub,
    timer,
    panels,
    params,
    sound,
    onInteractive: () => {
      perfHud.markInteractive();
      // Game is signed in, configured and rendering — drop the loading splash.
      window.hideLoadingScreen?.();
    },
  });
  orchestratorRef = orchestrator;

  await orchestrator.start();
}

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function showFatal(message: string): void {
  // Drop the loading splash first — it sits above the fatal overlay.
  window.hideLoadingScreen?.();
  const overlay = document.getElementById('fatal-overlay');
  const msg = document.getElementById('fatal-message');
  if (msg) msg.textContent = message;
  if (overlay) overlay.style.display = 'flex';
}

window.addEventListener('error', (e) => logError('[window.error]', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  logError('[unhandledrejection]', e.reason),
);

bootstrap().catch((e) => {
  logError('[main] bootstrap crashed', e);
  showFatal(`Bootstrap failed: ${(e as Error)?.message ?? String(e)}`);
});
